import React, {memo, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import styled from "styled-components"
import {api} from "../api/api";
import {usePageTitle} from "../hooks/usePageTitle";
import {RootState} from "../store/rootReducer";
import {ThemeProps} from "../styles/theme";
import {NavigationProps} from "../utils/navigationProps";
import {SignInError} from "./auth.interface";
import {authActions} from "./auth.slice";
import {loadGoogleIdentity} from "./googleSignIn";

const LoginFormRoot = styled.div`
    width: 100%;
    height: 100%;
    position: fixed;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const Panel = styled.div<ThemeProps>`
    font-size: 0.8em;
    padding: 25px;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 300px;

    background-color: ${props => props.theme.primaryBackground};
    max-width: 80%;
    border-radius: 4px;
    box-sizing: border-box;
    border: ${props => props.theme.primaryBorder} 2px solid;
    box-shadow: 4px 4px #E3E3E3;
`;

const Title = styled.div`
    font-size: 1.4em;
    margin-bottom: 20px;
`;

const ButtonSlot = styled.div`
    min-height: 44px;
`;

const ErrorMessage = styled.div<ThemeProps>`
    margin-top: 15px;
    color: ${props => props.theme.dangerColor || '#dc3545'};
    font-size: 0.9em;
    text-align: center;
`;

const SIGN_IN_ERRORS: Record<SignInError, string> = {
    denied: 'This Google account does not have access.',
    failed: 'Sign-in failed. Please try again.',
};

const LoginFormComponent: React.FC<NavigationProps> = () => {
    usePageTitle('Memkeep');
    const dispatch = useDispatch();
    const signInError = useSelector((state: RootState) => state.auth.signInError);
    const [setupError, setSetupError] = useState<string | null>(null);
    const buttonSlot = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let cancelled = false;

        const renderSignInButton = async () => {
            try {
                const {clientId} = await api.authConfig();
                if (clientId === null || clientId === '') {
                    throw new Error('Google sign-in is not configured on the server.');
                }

                const google = await loadGoogleIdentity();
                if (cancelled || buttonSlot.current === null) {
                    return;
                }

                google.initialize({
                    client_id: clientId,
                    callback: response => {
                        if (response.credential !== undefined && response.credential !== '') {
                            dispatch(authActions.signInWithGoogle(response.credential));
                        }
                    },
                });

                google.renderButton(buttonSlot.current, {
                    theme: 'outline',
                    size: 'large',
                    text: 'signin_with',
                    shape: 'rectangular',
                    width: 250,
                });
            } catch (error) {
                if (!cancelled) {
                    setSetupError(error instanceof Error ? error.message : 'Google sign-in is unavailable.');
                }
            }
        };

        renderSignInButton();

        return () => {
            cancelled = true;
        };
    }, [dispatch]);

    const message = setupError !== null
        ? setupError
        : signInError !== null ? SIGN_IN_ERRORS[signInError] : null;

    return (
        <LoginFormRoot>
            <Panel data-testid="login-panel">
                <Title>Memkeep</Title>
                <ButtonSlot ref={buttonSlot} data-testid="google-signin" />
                {message !== null && (
                    <ErrorMessage data-testid="login-error">{message}</ErrorMessage>
                )}
            </Panel>
        </LoginFormRoot>
    );
}

LoginFormComponent.displayName = 'LoginFormComponent';

const LoginForm = memo(LoginFormComponent);
LoginForm.displayName = 'LoginForm';

export {LoginForm};
